import { areFlightSearchEndpointsSame, flightAirportsAreSame } from 'legacyfrontend/helpers/flightAirportsAreSame';

export const FLIGHT_VALIDATION_CODES = {
  INCOMPLETE_DATA: 'INCOMPLETE_DATA',
  INVALID_ORIGIN: 'INVALID_ORIGIN',
  INVALID_DESTINATION: 'INVALID_DESTINATION',
  SAME_ORIGIN_DESTINATION: 'SAME_ORIGIN_DESTINATION',
  INVALID_DEPARTURE_DATE: 'INVALID_DEPARTURE_DATE',
  INVALID_RETURN_DATE: 'INVALID_RETURN_DATE',
  INVALID_PASSENGERS: 'INVALID_PASSENGERS',
};

/**
 * @typedef {{ code: string, message: string }} ValidationError
 */

function isValidDate(value) {
  if (value == null || value === '') return false;
  const t = value instanceof Date ? value : new Date(value);
  return !Number.isNaN(t.getTime());
}

export function validateFlightsSearchFromForm(input) {
  if (!input) {
    return { code: FLIGHT_VALIDATION_CODES.INCOMPLETE_DATA, message: 'Por favor completá los datos de la búsqueda.' };
  }
  if (!input.origin?.cityCode || !String(input.origin.cityName || '').trim()) {
    return { code: FLIGHT_VALIDATION_CODES.INVALID_ORIGIN, message: 'Por favor seleccione un origen válido' };
  }
  if (!input.destination?.cityCode || !String(input.destination.cityName || '').trim()) {
    return { code: FLIGHT_VALIDATION_CODES.INVALID_DESTINATION, message: 'Por favor seleccione un destino válido' };
  }
  const oEp = input.origin.cityCode || input.origin.airportCode;
  const dEp = input.destination.cityCode || input.destination.airportCode;
  if (
    flightAirportsAreSame(input.origin, input.destination) ||
    areFlightSearchEndpointsSame(oEp, dEp)
  ) {
    return { code: FLIGHT_VALIDATION_CODES.SAME_ORIGIN_DESTINATION, message: 'No podés realizar una búsqueda con el mismo origen y destino.' };
  }
  if (!isValidDate(input.departureDate)) {
    return { code: FLIGHT_VALIDATION_CODES.INVALID_DEPARTURE_DATE, message: 'Por favor seleccione una fecha de salida válida' };
  }
  if (!input.isOnlyOneWay && !isValidDate(input.returnDate)) {
    return { code: FLIGHT_VALIDATION_CODES.INVALID_RETURN_DATE, message: 'Por favor seleccione una fecha de regreso válida' };
  }
  return null;
}

const FLIGHT_FIELD_LABELS = {
  ORIGIN: 'origen',
  DESTINATION: 'destino',
  DATES: 'fechas',
};

/**
 * Returns an array of Spanish field labels for required fields that are
 * missing or invalid in the given flight search form input.
 *
 * Passengers are intentionally excluded because `validateFlightsSearchFromForm`
 * does not validate them and the submit handler auto-fills a default.
 *
 * @param {object|null} input
 * @returns {string[]}
 */
export function getMissingFlightFieldLabels(input) {
  const labels = [];
  if (!input || !input.origin?.cityCode || !String(input.origin.cityName || '').trim()) {
    labels.push(FLIGHT_FIELD_LABELS.ORIGIN);
  }
  if (!input || !input.destination?.cityCode || !String(input.destination.cityName || '').trim()) {
    labels.push(FLIGHT_FIELD_LABELS.DESTINATION);
  }
  if (!input || !isValidDate(input.departureDate) || (!input.isOnlyOneWay && !isValidDate(input.returnDate))) {
    labels.push(FLIGHT_FIELD_LABELS.DATES);
  }
  return labels;
}

export function validateFlightsSearchFromUrlParams({
  originId,
  originName,
  destinationId,
  destinationName,
  departureDate,
  returnDate,
  isOnlyOneWay,
  passengers,
}) {
  const oId = originId != null ? String(originId).trim() : '';
  const oName = originName != null ? String(originName).trim() : '';
  const dId = destinationId != null ? String(destinationId).trim() : '';
  const dName = destinationName != null ? String(destinationName).trim() : '';
  if (!oId || !oName) {
    return { code: FLIGHT_VALIDATION_CODES.INVALID_ORIGIN, message: 'Por favor seleccione un origen válido' };
  }
  if (!dId || !dName) {
    return { code: FLIGHT_VALIDATION_CODES.INVALID_DESTINATION, message: 'Por favor seleccione un destino válido' };
  }
  if (areFlightSearchEndpointsSame(oId, dId)) {
    return { code: FLIGHT_VALIDATION_CODES.SAME_ORIGIN_DESTINATION, message: 'No podés realizar una búsqueda con el mismo origen y destino.' };
  }
  if (!departureDate || !String(departureDate).trim()) {
    return { code: FLIGHT_VALIDATION_CODES.INVALID_DEPARTURE_DATE, message: 'Por favor seleccione una fecha de salida válida' };
  }
  if (!isValidDate(departureDate)) {
    return { code: FLIGHT_VALIDATION_CODES.INVALID_DEPARTURE_DATE, message: 'Por favor seleccione una fecha de salida válida' };
  }
  const oneWay = isOnlyOneWay === 'true' || isOnlyOneWay === true;
  if (!oneWay) {
    if (!returnDate || !String(returnDate).trim()) {
      return { code: FLIGHT_VALIDATION_CODES.INVALID_RETURN_DATE, message: 'Por favor seleccione una fecha de regreso válida' };
    }
    if (!isValidDate(returnDate)) {
      return { code: FLIGHT_VALIDATION_CODES.INVALID_RETURN_DATE, message: 'Por favor seleccione una fecha de regreso válida' };
    }
  }
  if (!passengers || (Array.isArray(passengers) && passengers.length === 0)) {
    return { code: FLIGHT_VALIDATION_CODES.INVALID_PASSENGERS, message: 'Por favor indicá la cantidad de pasajeros' };
  }
  return null;
}
